import { metricColor } from "@/modules/analytics/utils/metricColor";
import { cn } from "@/lib/utils";
import { Task } from "@repo/schema";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { MediaListItem, MediaListItemDate } from "./MediaListItem";

export interface TaskMetrics {
  meanConfidence?: number | null;
  meanIou?: number | null;
  precision?: number | null;
  recall?: number | null;
}

export interface TaskListItemProps {
  task: Pick<Task, "id" | "iid" | "createdAt">;
  /** Image URL — override lets callers show a local blob instead of task.thumbnailUrl. */
  imageSrc: string;
  imageAlt?: string;
  selected?: boolean;
  onClick?: () => void;
  rightSlot?: ReactNode;
  /** Sits beside `#iid` and never truncates — used for the awaiting-review chip. */
  titleSlot?: ReactNode;
  /** Optional confidence-report metrics shown as small colored badges below the date. */
  metrics?: TaskMetrics;
  /** Reserve left-edge space for a caller-rendered checkbox. See MediaListItem. */
  leadingInset?: boolean;
}

const fmtPct = (v: number) => `${(v * 100).toFixed(0)}%`;

const MetricBadge = ({
  label,
  value,
}: {
  label: string;
  value: number | null | undefined;
}) => {
  if (value == null) return null;
  return (
    <span
      className={cn(
        "rounded px-1 py-0.5 font-mono text-[9px] leading-none",
        metricColor(value),
      )}
    >
      {label} {fmtPct(value)}
    </span>
  );
};

const TaskMetricsBadges = ({ metrics }: { metrics: TaskMetrics }) => {
  const { t } = useTranslation("common");
  const { meanConfidence, meanIou, precision, recall } = metrics;
  if (meanConfidence == null && meanIou == null && precision == null && recall == null) return null;
  return (
    <div className="flex items-center gap-0.5 flex-wrap">
      <MetricBadge label={t("taskMetrics.confidence")} value={meanConfidence} />
      <MetricBadge label="IoU" value={meanIou} />
      <MetricBadge label={t("taskMetrics.precision")} value={precision} />
      <MetricBadge label={t("taskMetrics.recall")} value={recall} />
    </div>
  );
};

/**
 * Task row for the annotate + capture pages. Thin wrapper around
 * MediaListItem that fixes the image dimensions + emerald-border-on-select
 * treatment shared by both lists.
 */
export const TaskListItem = ({
  task,
  imageSrc,
  imageAlt,
  selected,
  onClick,
  rightSlot,
  titleSlot,
  metrics,
  leadingInset,
}: TaskListItemProps) => (
  <MediaListItem
    selected={selected}
    onClick={onClick}
    rightSlot={rightSlot}
    titleSlot={titleSlot}
    leadingInset={leadingInset}
    image={
      <img
        src={imageSrc}
        alt={imageAlt ?? `#${task.iid}`}
        className={cn(
          "h-[52px] w-[60px] shrink-0 rounded bg-muted object-cover",
          selected && "border border-emerald-500",
        )}
      />
    }
    title={`#${task.iid}`}
    subtitle={
      <div className="flex flex-col gap-0.5">
        <MediaListItemDate iso={task.createdAt} />
        {metrics && <TaskMetricsBadges metrics={metrics} />}
      </div>
    }
  />
);
